import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import SectionLabel from '../ui/SectionLabel';
import GlassCard from '../ui/GlassCard';

const testimonials = [
  { id: 1, name: 'Marco Rossi', role: 'CTO, TechVentures Italy', text: 'ABCI delivered an exceptional DeFi platform that exceeded our expectations. Their expertise in smart contract development and security auditing is truly world-class.', avatar: 'MR' },
  { id: 2, name: 'Priya Sharma', role: 'Founder, DataMinds India', text: 'The AI recommendation system ABCI built increased our user engagement by 40%. Their team truly understands machine learning at scale and delivered ahead of schedule.', avatar: 'PS' },
  { id: 3, name: 'Jean-Pierre Dubois', role: 'Director, InnovateFrance', text: 'Working with ABCI on our blockchain supply chain solution was a game-changer. Professional, knowledgeable, and delivered a production-ready system on time.', avatar: 'JD' },
];

export default function Testimonials() {
  const [idx, setIdx] = useState(0);
  const next = () => setIdx(p => (p + 1) % testimonials.length);
  const prev = () => setIdx(p => (p - 1 + testimonials.length) % testimonials.length);

  return (
    <section className="relative py-24 md:py-32" style={{ background: 'rgba(5,5,16,0.4)', backdropFilter: 'blur(4px)' }}>
      <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-8">
        <SectionLabel>Testimonials</SectionLabel>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="text-[clamp(2rem,4vw,3.5rem)] font-bold font-space text-[#F0F0F5] tracking-[-0.02em] mb-12 md:mb-16"
        >
          What Our Clients Say
        </motion.h2>

        {/* Desktop: 3-card grid */}
        <div className="hidden md:grid grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <GlassCard key={t.id} delay={i * 0.1} className="p-6 md:p-8">
              <Quote className="w-8 h-8 text-[#00D4FF]/15 mb-4" />
              <div className="flex gap-1 mb-4">
                {Array.from({ length: 5 }).map((_, j) => (
                  <Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-[#8A8AA3] text-sm leading-relaxed mb-6 italic">"{t.text}"</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00D4FF] to-[#7B61FF] flex items-center justify-center text-white text-sm font-bold">{t.avatar}</div>
                <div>
                  <p className="text-[#F0F0F5] text-sm font-semibold">{t.name}</p>
                  <p className="text-[#4A4A65] text-xs">{t.role}</p>
                </div>
              </div>
            </GlassCard>
          ))}
        </div>

        {/* Mobile: Carousel */}
        <div className="md:hidden">
          <AnimatePresence mode="wait">
            <motion.div key={idx} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.3 }}
              className="p-6 rounded-2xl bg-[rgba(255,255,255,0.03)] backdrop-blur-xl border border-[rgba(255,255,255,0.06)]">
              <Quote className="w-8 h-8 text-[#00D4FF]/15 mb-4" />
              <div className="flex gap-1 mb-4">{Array.from({ length: 5 }).map((_, j) => <Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" />)}</div>
              <p className="text-[#8A8AA3] text-sm leading-relaxed mb-6 italic">"{testimonials[idx].text}"</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00D4FF] to-[#7B61FF] flex items-center justify-center text-white text-sm font-bold">{testimonials[idx].avatar}</div>
                <div>
                  <p className="text-[#F0F0F5] text-sm font-semibold">{testimonials[idx].name}</p>
                  <p className="text-[#4A4A65] text-xs">{testimonials[idx].role}</p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
          <div className="flex items-center justify-center gap-4 mt-6">
            <button onClick={prev} className="w-10 h-10 rounded-full bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.06)] flex items-center justify-center text-[#8A8AA3] hover:text-white" aria-label="Previous"><ChevronLeft className="w-4 h-4" /></button>
            <div className="flex gap-2">{testimonials.map((_, i) => <button key={i} onClick={() => setIdx(i)} className={`w-2 h-2 rounded-full transition-all ${i === idx ? 'bg-[#00D4FF] w-6' : 'bg-[rgba(255,255,255,0.15)]'}`} aria-label={`Testimonial ${i+1}`} />)}</div>
            <button onClick={next} className="w-10 h-10 rounded-full bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.06)] flex items-center justify-center text-[#8A8AA3] hover:text-white" aria-label="Next"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      </div>
    </section>
  );
}
